import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SmartIrrigationService } from 'src/api/services';
import { SmartIrrigationControllerDashboard, SmartIrrigationDashboard, SmartIrrigationModel, SmartIrrigationRootNodeDto, SmartIrrigationWaterMeter, SmartIrrigationWaterTank, SmartIrrigationWeatherStation } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { forkJoin } from 'rxjs';

interface ParkChildSummary {
  id: number;
  name: string;
  controllerCount: number;
  activeAlarmCount: number;
  sensorCount: number;
  weatherStationCount: number;
  irrigationActive: boolean | null;
  status: 'alert' | 'warning' | 'normal';
}

interface ParkWeatherMetric {
  label: string;
  value: string;
  unit: string;
  status: 'red' | 'amber' | 'green';
  stationName: string;
  stationUri: string;
}

interface ParkCard {
  id: number;
  name: string;
  controllerCount: number;
  activeAlarmCount: number;
  weatherMetrics: ParkWeatherMetric[];
  waterMeter: ParkWaterMeter | null;
  tankLevel: ParkTankLevel | null;
  children: ParkChildSummary[];
}

interface ParkWaterMeter {
  usage: number | null;
  allocation: number | null;
  usagePercentage: number | null;
  barColor: 'blue' | 'red';
}

interface ParkTankLevel {
  percentage: number;
  barColor: 'green' | 'amber' | 'red';
}

@Component({
  selector: 'app-smart-irrigation-parks',
  templateUrl: './parks.component.html',
  styleUrls: ['./parks.component.scss']
})
export class SmartIrrigationParksComponent implements OnInit {
  loading = true;
  lastRefresh: Date | null = null;
  parks: ParkCard[] = [];
  scopedNodeId: number | null = null;
  scopedParkName: string | null = null;

  constructor(
    private app: ApplicationConfigService,
    private irrigation: SmartIrrigationService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const nodeId = this.route.snapshot.paramMap.get('nodeId');
    this.scopedNodeId = nodeId != null ? Number(nodeId) : null;
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    forkJoin({
      rootNode: this.irrigation.SmartIrrigationGetRootNode(this.app.customerId),
      dashboard: this.irrigation.SmartIrrigationGetDashboard(this.app.customerId),
      model: this.irrigation.SmartIrrigationGetModel(this.app.customerId)
    }).subscribe({
      next: ({ rootNode, dashboard, model }) => {
        const scopedNode = this.scopedNodeId != null ? this.findNodeById(rootNode, this.scopedNodeId) : null;
        this.scopedParkName = scopedNode?.name || null;
        this.parks = this.findParks(scopedNode || rootNode, dashboard, model);
        this.lastRefresh = new Date();
        this.loading = false;
      },
      error: () => {
        this.parks = [];
        this.loading = false;
      }
    });
  }

  private findNodeById(node: SmartIrrigationRootNodeDto, id: number): SmartIrrigationRootNodeDto | null {
    if (node.id === id) {
      return node;
    }
    for (const child of node.childNodes || []) {
      const found = this.findNodeById(child, id);
      if (found) {
        return found;
      }
    }
    return null;
  }

  viewPark(park: ParkCard): void {
    this.router.navigate(['/smart-irrigation/site', park.id]);
  }

  viewChild(child: ParkChildSummary): void {
    if (child.controllerCount > 0) {
      this.router.navigate(['/smart-irrigation/site', child.id]);
    }
  }

  viewWeatherTrend(park: ParkCard): void {
    this.router.navigate(['/smart-irrigation/weather-trend', park.id]);
  }

  viewWaterUsage(park: ParkCard): void {
    this.router.navigate(['/smart-irrigation/water-trend', park.id]);
  }

  private findParks(rootNode: SmartIrrigationRootNodeDto, dashboard: SmartIrrigationDashboard, model: SmartIrrigationModel): ParkCard[] {
    const controllersBySiteUri = new Map<string, SmartIrrigationControllerDashboard>();
    (dashboard.irrigationControllers || []).forEach(controller => {
      if (controller.siteUri) {
        controllersBySiteUri.set(this.normalize(controller.siteUri), controller);
      }
    });
    const waterMetersBySiteUri = new Map<string, SmartIrrigationWaterMeter>();
    Object.values(model?.waterMeters || {}).forEach(meter => {
      if (meter.siteUri) {
        waterMetersBySiteUri.set(this.normalize(meter.siteUri), meter);
      }
    });
    const waterTanksBySiteUri = new Map<string, SmartIrrigationWaterTank>();
    Object.values(model?.waterTanks || {}).forEach(tank => {
      if (tank.siteUri) {
        waterTanksBySiteUri.set(this.normalize(tank.siteUri), tank);
      }
    });
    const parks: ParkCard[] = [];
    const visit = (node: SmartIrrigationRootNodeDto): void => {
      const children = node.childNodes || [];
      if (children.some(child => this.nodeContainsController(child))) {
        const parkChildren = children.map(child => this.buildChildSummary(child, controllersBySiteUri, model));
        parks.push({
          id: node.id,
          name: node.name || `Park ${node.id}`,
          controllerCount: parkChildren.reduce((count, child) => count + child.controllerCount, 0),
          activeAlarmCount: parkChildren.reduce((count, child) => count + child.activeAlarmCount, 0),
          weatherMetrics: this.buildParkWeatherMetrics(node, dashboard.weatherStations || []),
          waterMeter: this.buildParkWaterMeter(node, waterMetersBySiteUri),
          tankLevel: this.buildParkTankLevel(node, waterTanksBySiteUri),
          children: parkChildren
        });
      }
      children.forEach(visit);
    };

    visit(rootNode);
    return parks;
  }

  private nodeContainsController(node: SmartIrrigationRootNodeDto): boolean {
    return (node.associatedDevices || []).some(device =>
      (device.deviceTypeName || '').toLowerCase().includes('controller')
    );
  }

  private buildChildSummary(
    node: SmartIrrigationRootNodeDto,
    controllersBySiteUri: Map<string, SmartIrrigationControllerDashboard>,
    model: SmartIrrigationModel
  ): ParkChildSummary {
    const devices = node.associatedDevices || [];
    const controllerUris = devices
      .filter(device => this.isController(device.deviceTypeName))
      .map(device => this.normalize(device.uri))
      .filter(uri => !!uri);
    const controllers = controllerUris
      .map(uri => controllersBySiteUri.get(uri))
      .filter((controller): controller is SmartIrrigationControllerDashboard => !!controller);
    const directSensorCount = devices.filter(device => this.isSoilSensor(device.deviceTypeName)).length;
    const sensorCount = directSensorCount > 0
      ? directSensorCount
      : Object.values(model?.soilMoistureProbes || {}).filter(probe =>
          controllers.some(controller => this.normalize(controller.siteUri) === this.normalize(probe.siteUri))
        ).length;
    const hasAlert = controllers.some(controller =>
      (controller.alarms || []).some(alarm => alarm.level <= 1) || controller.currentState?.value !== controller.expectedState?.value
    );
    const hasWarning = controllers.some(controller => (controller.alarms || []).some(alarm => alarm.level >= 2));
    const irrigationStates = controllers
      .map(controller => controller.currentIrrigationState?.value)
      .filter((state): state is boolean => state != null);
    const irrigationActive = irrigationStates.length > 0
      ? irrigationStates.some(state => state)
      : null;

    return {
      id: node.id,
      name: node.name || `Node ${node.id}`,
      controllerCount: controllerUris.length,
      activeAlarmCount: controllers.reduce((count, controller) => count + (controller.alarms || []).length, 0),
      sensorCount,
      weatherStationCount: devices.filter(device => (device.deviceTypeName || '').toLowerCase().includes('weather')).length,
      irrigationActive,
      status: hasAlert ? 'alert' : hasWarning ? 'warning' : 'normal'
    };
  }

  private buildParkWeatherMetrics(
    node: SmartIrrigationRootNodeDto,
    weatherStations: SmartIrrigationWeatherStation[]
  ): ParkWeatherMetric[] {
    const weatherDevices = (node.associatedDevices || []).filter(device =>
      (device.deviceTypeName || '').toLowerCase().includes('weather')
    );
    const station = weatherStations.find(weatherStation => weatherDevices.some(device => {
      const deviceKey = this.normalize(device.uri);
      return deviceKey && (deviceKey === this.normalize(weatherStation.deviceEui) || deviceKey === this.normalize(weatherStation.siteUri));
    }));

    if (!station) {
      return [];
    }

    return [
      this.parkWeatherMetric('Wind Speed', station.currentWindSpeed?.value, 'm/s', value => value > 17 ? 'red' : value > 11 ? 'amber' : 'green', station),
      this.parkWeatherMetric('Rainfall (24h)', station.currentRainfall?.value, 'mm', value => value > 50 ? 'red' : value > 20 ? 'amber' : 'green', station),
      this.parkWeatherMetric('Temperature', station.currentTemperature?.value, '°C', () => 'green', station),
      this.parkWeatherMetric('Humidity', station.currentHumidity?.value, '%', () => 'green', station)
    ];
  }

  private buildParkWaterMeter(
    node: SmartIrrigationRootNodeDto,
    waterMetersBySiteUri: Map<string, SmartIrrigationWaterMeter>
  ): ParkWaterMeter | null {
    const waterMeterDevice = (node.associatedDevices || []).find(device =>
      (device.deviceTypeName || '').toLowerCase().includes('water meter')
    );
    const meter = waterMeterDevice
      ? waterMetersBySiteUri.get(this.normalize(waterMeterDevice.uri))
      : undefined;
    if (!meter) {
      return null;
    }

    const usage = meter.totalWaterUsage?.value ?? null;
    const allocation = node.annualWaterAllowance ?? null;
    const usagePercentage = usage != null && allocation != null && allocation > 0
      ? usage / allocation * 100
      : null;
    return {
      usage,
      allocation,
      usagePercentage,
      barColor: usagePercentage != null && usagePercentage > 100 ? 'red' : 'blue'
    };
  }

  private buildParkTankLevel(
    node: SmartIrrigationRootNodeDto,
    waterTanksBySiteUri: Map<string, SmartIrrigationWaterTank>
  ): ParkTankLevel | null {
    const tankDevice = (node.associatedDevices || []).find(device =>
      (device.deviceTypeName || '').toLowerCase().includes('water tank')
    );
    const tank = tankDevice?.uri
      ? waterTanksBySiteUri.get(this.normalize(tankDevice.uri))
      : undefined;
    const percentage = tank?.currentTankCapacity?.value;
    if (percentage == null || !Number.isFinite(percentage)) {
      return null;
    }

    const clampedPercentage = Math.max(0, Math.min(100, percentage));
    return {
      percentage: clampedPercentage,
      barColor: clampedPercentage <= 20 ? 'red' : clampedPercentage <= 50 ? 'amber' : 'green'
    };
  }

  private parkWeatherMetric(
    label: string,
    value: number,
    unit: string,
    getStatus: (value: number) => 'red' | 'amber' | 'green',
    station: SmartIrrigationWeatherStation
  ): ParkWeatherMetric {
    return {
      label,
      value: value != null ? value.toFixed(1) : 'N/A',
      unit,
      status: value != null ? getStatus(value) : 'green',
      stationName: station.description || station.name || station.deviceEui || 'Weather Station',
      stationUri: station.siteUri || station.deviceEui || 'Unavailable'
    };
  }

  private isController(typeName?: string): boolean {
    return (typeName || '').toLowerCase().includes('controller');
  }

  private isSoilSensor(typeName?: string): boolean {
    const type = (typeName || '').toLowerCase();
    return type.includes('soil') || type.includes('probe');
  }

  private normalize(value?: string): string {
    return (value || '').trim().toLowerCase();
  }
}
